import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  FileCheck, Download, GitCompare, Clock, FileText, Shield, 
  AlertTriangle, TrendingUp, TrendingDown, Minus, ChevronRight,
  RefreshCw, Eye, Calendar, Sparkles, Copy, ChevronDown, ChevronUp
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const ComplianceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [exports, setExports] = useState([]);
  const [selectedExportA, setSelectedExportA] = useState(null);
  const [selectedExportB, setSelectedExportB] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Sprint 22: AI Explain Changes state
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explanationExpanded, setExplanationExpanded] = useState(true);
  const explanationCacheRef = useRef({}); // 15-minute client-side memoization

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryRes, exportsRes] = await Promise.all([
        api.get('/compliance/dashboard/summary'),
        api.get('/compliance/dashboard/exports?limit=20')
      ]);
      
      setDashboardData(summaryRes.data);
      setExports(exportsRes.data.exports || []);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load compliance dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!selectedExportA || !selectedExportB) {
      toast.error('Please select two exports to compare');
      return;
    }
    
    if (selectedExportA === selectedExportB) {
      toast.error('Please select different exports to compare');
      return;
    }
    
    try {
      setComparing(true);
      const response = await api.post('/compliance/dashboard/compare', {
        export_id_a: selectedExportA,
        export_id_b: selectedExportB
      });
      setCompareResult(response.data);
      setActiveTab('compare');
      toast.success('Comparison ready');
      // Clear any previous explanation when new comparison is done
      setExplanation(null);
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error('Failed to compare exports');
    } finally {
      setComparing(false);
    }
  };

  // Sprint 22: AI Explain Changes handler
  const handleExplainChanges = async () => {
    if (!selectedExportA || !selectedExportB) {
      toast.error('Please select exports to compare first');
      return;
    }
    
    // Check 15-minute cache
    const cacheKey = `${selectedExportA}-${selectedExportB}`;
    const cached = explanationCacheRef.current[cacheKey];
    if (cached && (Date.now() - cached.timestamp) < 15 * 60 * 1000) {
      setExplanation(cached.data);
      setExplanationExpanded(true);
      toast.info('Using cached explanation');
      return;
    }
    
    try {
      setExplaining(true);
      toast.info('Generating AI explanation...');
      
      const response = await api.post('/compliance/dashboard/compare/explain', {
        export_id_a: selectedExportA,
        export_id_b: selectedExportB
      });
      
      setExplanation(response.data);
      setExplanationExpanded(true);
      
      // Cache for 15 minutes
      explanationCacheRef.current[cacheKey] = {
        data: response.data,
        timestamp: Date.now()
      };
      
      toast.success(`Explanation generated${response.data.model_used === 'mock' ? ' (mock mode)' : ''}`);
    } catch (error) {
      console.error('Explain error:', error);
      toast.error('Failed to generate explanation');
    } finally {
      setExplaining(false);
    }
  };

  const handleCopyExplanation = () => {
    if (explanation?.explanation) {
      navigator.clipboard.writeText(explanation.explanation);
      toast.success('Explanation copied to clipboard');
    }
  };

  const handleExportPack = async (mode = 'full') => {
    try {
      toast.info('Generating compliance pack...');
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/governance/compliance-pack?format=zip&mode=${mode}`, '_blank');
    } catch (error) {
      toast.error('Failed to generate export');
    }
  };

  const getDeltaIcon = (delta) => {
    if (delta > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (delta < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getDeltaBadge = (delta, deltaPercent) => {
    if (delta === 0) return null;
    const isPositive = delta > 0;
    return (
      <Badge variant={isPositive ? 'default' : 'destructive'} className="text-xs">
        {isPositive ? '+' : ''}{delta} ({deltaPercent}%)
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)]">Compliance Pack Dashboard</h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            Track governance exports and compare compliance snapshots
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExportPack('summary')}>
            <FileText className="w-4 h-4 mr-2" />
            Summary PDF
          </Button>
          <Button onClick={() => handleExportPack('full')}>
            <Download className="w-4 h-4 mr-2" />
            Full Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="exports" data-testid="tab-exports">Export History</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">Compare Mode</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Section Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {dashboardData?.section_tiles?.map((tile) => (
              <Card key={tile.section} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-[var(--clr-text-secondary)] uppercase tracking-wider">
                        {tile.label}
                      </p>
                      <p className="text-2xl font-bold text-[var(--clr-text-primary)] mt-1">
                        {tile.count}
                      </p>
                    </div>
                    {getDeltaIcon(tile.delta)}
                  </div>
                  {tile.delta !== 0 && (
                    <div className="mt-2">
                      {getDeltaBadge(tile.delta, tile.delta_percent)}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileCheck className="w-4 h-4" />
                  Total Exports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{dashboardData?.quick_stats?.total_exports || 0}</p>
                <p className="text-xs text-[var(--clr-text-secondary)] mt-1">
                  All-time compliance packs generated
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Latest Export
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.quick_stats?.latest_export ? (
                  <>
                    <p className="text-lg font-semibold">
                      {new Date(dashboardData.quick_stats.latest_export.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-[var(--clr-text-secondary)] mt-1">
                      Hash: {dashboardData.quick_stats.latest_export.filter_hash}
                    </p>
                  </>
                ) : (
                  <p className="text-[var(--clr-text-secondary)]">No exports yet</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  This Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{dashboardData?.quick_stats?.export_trend || 0}</p>
                <p className="text-xs text-[var(--clr-text-secondary)] mt-1">
                  Exports in the last 7 days
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Exports Tab */}
        <TabsContent value="exports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Export History
              </CardTitle>
              <CardDescription>All compliance pack exports with checksums</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {exports.map((exp) => (
                  <div 
                    key={exp.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-[var(--clr-surface-subtle)] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[var(--clr-brand-primary)]/10 flex items-center justify-center">
                        <FileCheck className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                      </div>
                      <div>
                        <p className="font-medium text-[var(--clr-text-primary)]">
                          {exp.filter_hash}
                        </p>
                        <p className="text-sm text-[var(--clr-text-secondary)]">
                          {new Date(exp.created_at).toLocaleString()} • {exp.mode} • {exp.format}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{exp.file_count} files</Badge>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {exports.length === 0 && (
                  <p className="text-center text-[var(--clr-text-secondary)] py-8">
                    No exports yet. Generate your first compliance pack above.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Tab */}
        <TabsContent value="compare" className="space-y-6">
          {/* Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitCompare className="w-5 h-5" />
                Compare Exports
              </CardTitle>
              <CardDescription>Select two exports to compare side-by-side</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Export A (Baseline)</label>
                  <Select value={selectedExportA} onValueChange={setSelectedExportA}>
                    <SelectTrigger data-testid="select-export-a">
                      <SelectValue placeholder="Select first export" />
                    </SelectTrigger>
                    <SelectContent>
                      {exports.map((exp) => (
                        <SelectItem key={exp.id} value={exp.id}>
                          {exp.filter_hash} - {new Date(exp.created_at).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Export B (Compare)</label>
                  <Select value={selectedExportB} onValueChange={setSelectedExportB}>
                    <SelectTrigger data-testid="select-export-b">
                      <SelectValue placeholder="Select second export" />
                    </SelectTrigger>
                    <SelectContent>
                      {exports.map((exp) => (
                        <SelectItem key={exp.id} value={exp.id}>
                          {exp.filter_hash} - {new Date(exp.created_at).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleCompare} 
                  disabled={comparing || !selectedExportA || !selectedExportB}
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <GitCompare className="w-4 h-4 mr-2" />
                  )}
                  Compare
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Comparison Results */}
          {compareResult && (
            <Card>
              <CardHeader>
                <CardTitle>Comparison Results</CardTitle>
                <CardDescription>
                  {compareResult.export_a?.filter_hash} vs {compareResult.export_b?.filter_hash}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sprint 21: Heatmap Row - Quick view of section changes */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-[var(--clr-text-secondary)] mb-3">
                    Section Change Heatmap
                  </h4>
                  <div className="flex gap-1 overflow-x-auto pb-2">
                    {compareResult.sections?.map((section) => {
                      const hasChanges = section.delta !== 0 || section.added?.length > 0 || section.removed?.length > 0 || section.changed?.length > 0;
                      const changeIntensity = Math.min(100, Math.abs(section.delta_percent || 0));
                      
                      return (
                        <div 
                          key={section.section}
                          className="flex flex-col items-center"
                          title={`${section.section}: ${section.delta > 0 ? '+' : ''}${section.delta} (${section.delta_percent}%)`}
                        >
                          <div 
                            className={`w-16 h-8 rounded flex items-center justify-center text-xs font-medium transition-all ${
                              !hasChanges ? 'bg-gray-100 text-gray-500' :
                              section.delta > 0 ? 'bg-green-100 text-green-800 border border-green-300' :
                              section.delta < 0 ? 'bg-red-100 text-red-800 border border-red-300' :
                              'bg-amber-100 text-amber-800 border border-amber-300'
                            }`}
                            style={hasChanges ? { opacity: Math.max(0.5, changeIntensity / 100) } : {}}
                          >
                            {section.delta > 0 ? '+' : ''}{section.delta}
                          </div>
                          <span className="text-[10px] text-[var(--clr-text-secondary)] mt-1 truncate w-16 text-center capitalize">
                            {section.section.replace('_', ' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[var(--clr-text-secondary)]">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-green-200 border border-green-300" /> Added
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-amber-200 border border-amber-300" /> Modified
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-200 border border-red-300" /> Removed
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-gray-100" /> No Change
                    </span>
                    
                    {/* Sprint 22: Explain Changes AI Button */}
                    <div className="ml-auto">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleExplainChanges}
                        disabled={explaining}
                        data-testid="explain-changes-btn"
                      >
                        {explaining ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        Explain Changes (AI)
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sprint 22: AI Explanation Panel */}
                {explanation && (
                  <div className="mb-6 border rounded-lg bg-gradient-to-r from-purple-50 to-blue-50 overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 transition-colors"
                      onClick={() => setExplanationExpanded(!explanationExpanded)}
                    >
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        <div>
                          <h4 className="font-medium text-[var(--clr-text-primary)]">AI-Generated Summary</h4>
                          <p className="text-xs text-[var(--clr-text-secondary)]">
                            Generated {new Date(explanation.generated_at).toLocaleString()}
                            {explanation.model_used === 'mock' && ' (mock mode)'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={(e) => { e.stopPropagation(); handleCopyExplanation(); }}
                          data-testid="copy-explanation-btn"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                        {explanationExpanded ? (
                          <ChevronUp className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                        )}
                      </div>
                    </div>
                    
                    {explanationExpanded && (
                      <div className="px-4 pb-4 border-t bg-white/50">
                        <div className="prose prose-sm max-w-none mt-4">
                          <div className="whitespace-pre-wrap text-[var(--clr-text-primary)]">
                            {explanation.explanation}
                          </div>
                        </div>
                        
                        {explanation.governance_implications?.length > 0 && (
                          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <h5 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Governance Implications
                            </h5>
                            <ul className="text-sm text-amber-700 space-y-1">
                              {explanation.governance_implications.map((imp, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-amber-500">•</span>
                                  {imp}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{compareResult.summary?.total_added || 0}</p>
                    <p className="text-xs text-green-700">Added</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{compareResult.summary?.total_removed || 0}</p>
                    <p className="text-xs text-red-700">Removed</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{compareResult.summary?.total_changed || 0}</p>
                    <p className="text-xs text-amber-700">Changed</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{compareResult.summary?.net_change || 0}</p>
                    <p className="text-xs text-blue-700">Net Change</p>
                  </div>
                </div>

                {/* Section Accordions */}
                <Accordion type="multiple" className="space-y-2">
                  {compareResult.sections?.map((section) => (
                    <AccordionItem key={section.section} value={section.section} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="font-medium capitalize">{section.section.replace('_', ' ')}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-[var(--clr-text-secondary)]">
                              {section.count_a} → {section.count_b}
                            </span>
                            {getDeltaBadge(section.delta, section.delta_percent)}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        {/* Sprint 21: Enhanced diff view with better color coding */}
                        {/* Added Items */}
                        {section.added?.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-green-600 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              Added ({section.added.length})
                            </h4>
                            <div className="space-y-1">
                              {section.added.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="text-sm p-2 bg-green-50 rounded border-l-4 border-green-500 hover:bg-green-100 transition-colors">
                                  <span className="text-green-800">+ </span>
                                  {item.title || item.name || item.item_title || JSON.stringify(item).slice(0, 100)}
                                </div>
                              ))}
                              {section.added.length > 5 && (
                                <p className="text-xs text-green-600 pl-2">...and {section.added.length - 5} more</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Removed Items */}
                        {section.removed?.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-red-500" />
                              Removed ({section.removed.length})
                            </h4>
                            <div className="space-y-1">
                              {section.removed.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="text-sm p-2 bg-red-50 rounded border-l-4 border-red-500 hover:bg-red-100 transition-colors">
                                  <span className="text-red-800">− </span>
                                  {item.title || item.name || item.item_title || JSON.stringify(item).slice(0, 100)}
                                </div>
                              ))}
                              {section.removed.length > 5 && (
                                <p className="text-xs text-red-600 pl-2">...and {section.removed.length - 5} more</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Changed Items */}
                        {section.changed?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-amber-600 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-amber-500" />
                              Modified ({section.changed.length})
                            </h4>
                            <div className="space-y-1">
                              {section.changed.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="text-sm p-2 bg-amber-50 rounded border-l-4 border-amber-500 hover:bg-amber-100 transition-colors">
                                  <span className="text-amber-800">~ </span>
                                  <span className="font-medium">{item.key}</span>
                                  {item.fields_changed?.length > 0 && (
                                    <span className="text-[var(--clr-text-secondary)]">
                                      {' '}— Changed: {item.fields_changed.map((f, i) => (
                                        <span key={i} className="inline-flex items-center mx-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">
                                          {f}
                                        </span>
                                      ))}
                                    </span>
                                  )}
                                </div>
                              ))}
                              {section.changed.length > 5 && (
                                <p className="text-xs text-amber-600 pl-2">...and {section.changed.length - 5} more</p>
                              )}
                            </div>
                          </div>
                        )}

                        {!section.added?.length && !section.removed?.length && !section.changed?.length && (
                          <div className="flex items-center gap-2 text-[var(--clr-text-secondary)] text-sm p-3 bg-gray-50 rounded">
                            <div className="w-2 h-2 rounded-full bg-gray-400" />
                            No changes in this section
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceDashboard;
